import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import {
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  register as apiRegister,
  type Artist,
} from '../api'

type AuthContextType = {
  artist: Artist | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  register: (email: string, password: string, name: string, phone?: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [artist, setArtist] = useState<Artist | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchMe()
      .then(setArtist)
      .catch(() => setArtist(null))
      .finally(() => setLoading(false))
  }, [])

  return (
    <AuthContext.Provider
      value={{
        artist,
        loading,
        login: async (email, password) => setArtist(await apiLogin(email, password)),
        logout: async () => { await apiLogout(); setArtist(null) },
        register: async (email, password, name, phone) =>
          setArtist(await apiRegister(email, password, name, phone)),
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
